<template>
  <div class="consultant-activity">
    <h2>{{ t('consultantActivity.Title') }}</h2>

    <div v-if="loading" class="loading">{{ t('labels.Loading') }}...</div>
    <div v-else-if="error" class="error">{{ error }}</div>

    <div v-else class="content">
      <!-- Batch Selection Dropdown -->
      <div class="form-group">
        <label for="batch-dropdown">{{ t('consultantActivity.SelectBatch') }}</label>
        <DropDownList
          id="batch-dropdown"
          :data-items="batchList"
          v-model="selectedBatch"
          :text-field="'batchName'"
          :data-item-key="'batchNumber'"
          :default-item="defaultBatchItem"
          @change="onBatchChange"
        />
      </div>

      <!-- Tab Strip -->
      <kTabStrip @select="onTabSelect" :selected="selectedTab" class="activity-tabstrip">
        <!-- Adjustments Tab -->
        <TabStripTab :title="t('consultantActivity.AdjustmentsTab')">
          <div class="tab-content">
            <div v-if="reportData.length > 0" class="report-content">
              <Grid
                :data-items="paginatedData"
                :columns="gridColumns"
                :resizable="true"
                :sortable="true"
                :pageable="{
                  buttonCount: 5,
                  pageSizes: [10, 20, 50, 100],
                  info: true
                }"
                :skip="skip"
                :take="pageSize"
                :total="reportData.length"
                @pagechange="pageChange"
              />
            </div>
            <div v-else-if="selectedBatch" class="no-data">
              {{ t('consultantActivity.NoDataAvailable') }}
            </div>
          </div>
        </TabStripTab>

        <!-- Activity Tab -->
        <TabStripTab :title="t('consultantActivity.ActivityTab')">
          <div class="tab-content">
            <div v-if="activityReportData.length > 0" class="report-content">
              <Grid
                :data-items="activityPaginatedData"
                :columns="activityGridColumns"
                :resizable="true"
                :sortable="true"
                :pageable="{
                  buttonCount: 5,
                  pageSizes: [10, 20, 50, 100],
                  info: true
                }"
                :skip="activitySkip"
                :take="activityPageSize"
                :total="activityReportData.length"
                @pagechange="activityPageChange"
              />
            </div>
            <div v-else-if="selectedBatch" class="no-data">
              {{ t('consultantActivity.NoDataAvailable') }}
            </div>
          </div>
        </TabStripTab>

        <!-- Orders Tab -->
        <TabStripTab :title="t('consultantActivity.OrdersTab')">
          <div class="tab-content">
            <div class="orders-toolbar">
              <KButton
                @click="toggleIncludeTeam"
                :theme-color="includeTeam ? 'primary' : 'secondary'"
                class="team-toggle-button"
              >
                {{ includeTeam ? t('consultantActivity.RemoveTeam') : t('consultantActivity.IncludeTeam') }}
              </KButton>
            </div>
            <div v-if="ordersReportData.length > 0" class="report-content">
              <Grid
                :data-items="ordersPaginatedData"
                :columns="ordersGridColumns"
                :resizable="true"
                :sortable="true"
                :pageable="{
                  buttonCount: 5,
                  pageSizes: [10, 20, 50, 100],
                  info: true
                }"
                :skip="ordersSkip"
                :take="ordersPageSize"
                :total="ordersReportData.length"
                @pagechange="ordersPageChange"
              />
            </div>
            <div v-else-if="selectedBatch" class="no-data">
              {{ t('consultantActivity.NoDataAvailable') }}
            </div>
          </div>
        </TabStripTab>
      </kTabStrip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { DropDownList } from '@progress/kendo-vue-dropdowns'
import { TabStrip as kTabStrip, TabStripTab } from '@progress/kendo-vue-layout'
import { Grid } from '@progress/kendo-vue-grid'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import type { GridPageChangeEvent } from '@progress/kendo-vue-grid'
import { PWSHstBatchGetList, PwsBusinessDetailsGet } from '@/api/Business'
import { PwsDynamicReportGet, PwsDynamicReportExecute, type DynamicReportDefinition } from '@/api/ConsultantCorner'
import { resolveColumnWidth } from '@/utils/kendoHelper.ts'
import { FormatCurrency } from '@/services/currencyFormatter'

const { t } = useI18n()
const logger = useLogger()

const loading = ref(true)
const error = ref('')
const batchList = ref<any[]>([])
const selectedBatch = ref<any>(null)
const selectedTab = ref(0)

// Adjustments tab state
const reportDefinition = ref<DynamicReportDefinition | null>(null)
const reportData = ref<any[]>([])
const skip = ref(0)
const pageSize = ref(20)

// Activity tab state
const activityReportDefinition = ref<DynamicReportDefinition | null>(null)
const activityReportData = ref<any[]>([])
const activitySkip = ref(0)
const activityPageSize = ref(20)

// Orders tab state
const ordersReportDefinition = ref<DynamicReportDefinition | null>(null)
const ordersReportData = ref<any[]>([])
const ordersSkip = ref(0)
const ordersPageSize = ref(20)
const includeTeam = ref(false)

const defaultBatchItem = {
  batchName: t('consultantActivity.SelectBatchPlaceholder'),
  batchNumber: null
}

// Adjustments Tab - Build grid columns dynamically from report definition
const gridColumns = computed(() => {
  if (!reportDefinition.value) return []

  return reportDefinition.value.columns
    .filter(column => !column.isFilterOnly)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map(column => ({
      field: column.columnName,
      title: column.localizedColumnName,
      width: resolveColumnWidth('250px', column.localizedColumnName)
      // Date formatting is handled in the data transformation
    }))
})

// Adjustments Tab - Paginated data for grid
const paginatedData = computed(() => {
  const start = skip.value
  const end = Math.min(start + pageSize.value, reportData.value.length)
  return reportData.value.slice(start, end)
})

// Adjustments Tab - Handle page change
const pageChange = (event: GridPageChangeEvent) => {
  skip.value = event.page.skip
  pageSize.value = event.page.take
}

// Activity Tab - Build grid columns dynamically from report definition
const activityGridColumns = computed(() => {
  if (!activityReportDefinition.value) return []

  // Create columns with Field Name and Value
  return [
    {
      field: 'fieldName',
      title: t('consultantActivity.Metric'),
      width: resolveColumnWidth('300px', t('consultantActivity.Metric'))
    },
    {
      field: 'value',
      title: t('consultantActivity.Value'),
      width: resolveColumnWidth('200px', t('consultantActivity.Value'))
    }
  ]
})

// Activity Tab - Paginated data for grid
const activityPaginatedData = computed(() => {
  const start = activitySkip.value
  const end = Math.min(start + activityPageSize.value, activityReportData.value.length)
  return activityReportData.value.slice(start, end)
})

// Activity Tab - Handle page change
const activityPageChange = (event: GridPageChangeEvent) => {
  activitySkip.value = event.page.skip
  activityPageSize.value = event.page.take
}

// Orders Tab - Build grid columns dynamically from report definition
const ordersGridColumns = computed(() => {
  if (!ordersReportDefinition.value) return []

  return ordersReportDefinition.value.columns
    .filter(column => !column.isFilterOnly)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map(column => ({
      field: column.columnName,
      title: column.localizedColumnName,
      width: resolveColumnWidth('200px', column.localizedColumnName)
      // Date formatting is handled in the data transformation
    }))
})

// Orders Tab - Paginated data for grid
const ordersPaginatedData = computed(() => {
  const start = ordersSkip.value
  const end = Math.min(start + ordersPageSize.value, ordersReportData.value.length)
  return ordersReportData.value.slice(start, end)
})

// Orders Tab - Handle page change
const ordersPageChange = (event: GridPageChangeEvent) => {
  ordersSkip.value = event.page.skip
  ordersPageSize.value = event.page.take
}

// Load report definition for Adjustments tab
async function loadReportDefinition() {
  try {
    const definition = await PwsDynamicReportGet('BeeAdjustment')
    logger.debug('Adjustments report definition response:', definition)
    reportDefinition.value = definition.item
    logger.info('Adjustments report definition loaded:', definition.item.localizedReportName)
  } catch (err: any) {
    error.value = err.message || 'Failed to load adjustments report definition'
    logger.error('Error loading adjustments report definition:', err)
    throw err
  }
}

// Load report definition for Activity tab
async function loadActivityReportDefinition() {
  try {
    const definition = await PwsDynamicReportGet('BusinessDetails')
    logger.debug('Activity report definition response:', definition)
    activityReportDefinition.value = definition.item
    logger.info('Activity report definition loaded:', definition.item.localizedReportName)
    return definition.item
  } catch (err: any) {
    error.value = err.message || 'Failed to load activity report definition'
    logger.error('Error loading activity report definition:', err)
    throw err
  }
}

// Load report definition for Orders tab
async function loadOrdersReportDefinition() {
  try {
    const definition = await PwsDynamicReportGet('PersonalOrders')
    logger.debug('Orders report definition response:', definition)
    ordersReportDefinition.value = definition.item
    logger.info('Orders report definition loaded:', definition.item.localizedReportName)
  } catch (err: any) {
    error.value = err.message || 'Failed to load orders report definition'
    logger.error('Error loading orders report definition:', err)
    throw err
  }
}

// Execute Adjustments report with selected batch
async function executeReport(batchNumber: string) {
  if (!reportDefinition.value) {
    logger.warn('Cannot execute adjustments report: no report definition loaded')
    return
  }

  try {
    loading.value = true
    error.value = ''

    // Get columns to fetch (exclude filter-only columns)
    const columnsToFetch = reportDefinition.value.columns
      .filter((col: any) => !col.isFilterOnly)
      .map((col: any) => col.columnName)

    // Build filters with the batchNumber parameter
    const filters = [
      {
        columnName: 'BatchNumber',
        filterType: 'Equal',
        value: batchNumber
      }
    ]

    logger.debug('Executing adjustments report with filters:', filters)

    const result = await PwsDynamicReportExecute({
      reportId: 'BeeAdjustment',
      ColumnNames: columnsToFetch,
      Filters: filters
    })

    logger.debug('Adjustments report data:', result)

    // Transform date columns to European format (DD/MM/YYYY)
    const dateColumns = reportDefinition.value.columns
      .filter((col: any) => col.dataType === 'DateDataType')
      .map((col: any) => col.columnName)

    const transformedData = (result.Items || []).map((item: any) => {
      const transformedItem = { ...item }
      dateColumns.forEach((colName: string) => {
        if (transformedItem[colName]) {
          const date = new Date(transformedItem[colName])
          // Format as DD/MM/YYYY
          const day = String(date.getDate()).padStart(2, '0')
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const year = date.getFullYear()
          transformedItem[colName] = `${day}/${month}/${year}`
        }
      })

      // Format amount columns using centralized currency formatter
      Object.keys(transformedItem).forEach((key: string) => {
        if (key.toLowerCase().includes('amt') || key.toLowerCase().includes('amount')) {
          const value = transformedItem[key]
          if (value !== null && value !== undefined && value !== '') {
            const numericValue = typeof value === 'string' ? parseFloat(value) : Number(value)
            if (!isNaN(numericValue)) {
              transformedItem[key] = FormatCurrency(numericValue, null)
            }
          }
        }
      })

      return transformedItem
    })

    reportData.value = transformedData
    logger.info(`Loaded ${reportData.value.length} adjustments report rows`)

    // Reset pagination
    skip.value = 0
  } catch (err: any) {
    error.value = err.message || 'Failed to execute adjustments report'
    logger.error('Error executing adjustments report:', err)
  } finally {
    loading.value = false
  }
}

// Execute Activity report (BusinessDetails) with selected batch
async function executeActivityReport(batchNumber: string) {
  if (!activityReportDefinition.value) {
    logger.warn('Cannot execute activity report: no report definition loaded')
    return
  }

  try {
    loading.value = true
    error.value = ''

    // Get columns to fetch (exclude filter-only columns)
    const columnNames = activityReportDefinition.value.columns
      .filter((col: any) => !col.isFilterOnly)
      .map((col: any) => col.columnName)

    logger.debug('Executing activity report with batch:', batchNumber)
    logger.debug('Column names:', columnNames)

    const result = await PwsBusinessDetailsGet(columnNames, batchNumber)
    logger.debug('Activity report data:', result)

    // Transform the response from array of {fieldName, value} to array for grid
    const transformedData: any[] = []
    if (result && result.currentFields) {
      result.currentFields.forEach((field: any) => {
        let displayValue = field.value === 'Not Implemented' ? 0 : field.value
        
        // Format Sales Org Currency using centralized currency formatter
        if (field.format === 'Sales Org Currency' && displayValue !== null && displayValue !== undefined && displayValue !== '') {
          const numericValue = typeof displayValue === 'string' ? parseFloat(displayValue) : Number(displayValue)
          if (!isNaN(numericValue)) {
            displayValue = FormatCurrency(numericValue, null)
          }
        }
        
        transformedData.push({
          fieldName: field.localizedFieldName,
          value: displayValue
        })
      })
    }

    activityReportData.value = transformedData
    logger.info(`Loaded activity report with ${transformedData.length} fields`)

    // Reset pagination
    activitySkip.value = 0
  } catch (err: any) {
    error.value = err.message || 'Failed to execute activity report'
    logger.error('Error executing activity report:', err)
  } finally {
    loading.value = false
  }
}

// Execute Orders report with selected batch
async function executeOrdersReport(batchNumber: string) {
  if (!ordersReportDefinition.value) {
    logger.warn('Cannot execute orders report: no report definition loaded')
    return
  }

  try {
    loading.value = true
    error.value = ''

    // Get columns to fetch (exclude filter-only columns)
    const columnsToFetch = ordersReportDefinition.value.columns
      .filter((col: any) => !col.isFilterOnly)
      .map((col: any) => col.columnName)

    // Build filters with the batchNumber parameter
    const filters = [
      {
        columnName: 'BatchNumber',
        filterType: 'Equal',
        value: batchNumber
      }
    ]

    // Add IncludeTeam filter if enabled
    if (includeTeam.value) {
      filters.push({
        columnName: 'IncludeTeam',
        filterType: 'Equal',
        value: '1'
      })
    }

    logger.debug('Executing orders report with filters:', filters)

    const result = await PwsDynamicReportExecute({
      reportId: 'PersonalOrders',
      ColumnNames: columnsToFetch,
      Filters: filters
    })

    logger.debug('Orders report data:', result)

    // Transform date columns to European format (DD/MM/YYYY)
    const dateColumns = ordersReportDefinition.value.columns
      .filter((col: any) => col.dataType === 'DateDataType')
      .map((col: any) => col.columnName)

    const transformedData = (result.Items || []).map((item: any) => {
      const transformedItem = { ...item }
      dateColumns.forEach((colName: string) => {
        if (transformedItem[colName]) {
          const date = new Date(transformedItem[colName])
          // Format as DD/MM/YYYY
          const day = String(date.getDate()).padStart(2, '0')
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const year = date.getFullYear()
          transformedItem[colName] = `${day}/${month}/${year}`
        }
      })
      
      // Format OrderTotal using centralized currency formatter
      if (transformedItem.OrderTotal !== null && transformedItem.OrderTotal !== undefined && transformedItem.OrderTotal !== '') {
        const numericValue = typeof transformedItem.OrderTotal === 'string' ? parseFloat(transformedItem.OrderTotal) : Number(transformedItem.OrderTotal)
        if (!isNaN(numericValue)) {
          transformedItem.OrderTotal = FormatCurrency(numericValue, null)
        }
      }
      
      return transformedItem
    })

    ordersReportData.value = transformedData
    logger.info(`Loaded ${ordersReportData.value.length} orders report rows`)

    // Reset pagination
    ordersSkip.value = 0
  } catch (err: any) {
    error.value = err.message || 'Failed to execute orders report'
    logger.error('Error executing orders report:', err)
  } finally {
    loading.value = false
  }
}

// Fetch batch history data
async function loadBatchList() {
  try {
    loading.value = true
    error.value = ''

    // Request the 10 most recent batches sorted by history date descending
    const response = await PWSHstBatchGetList(10, 0)
    logger.debug('Batch history response:', response)

    if (response && response.items && response.items.length > 0) {
      batchList.value = response.items
      logger.info(`Loaded ${batchList.value.length} batches`)

      // Automatically select the most recent batch (first in the list)
      selectedBatch.value = response.items[0]
      logger.info('Auto-selected most recent batch:', selectedBatch.value.batchName)

      // Execute reports for all tabs with the most recent batch's batchNumber
      if (selectedBatch.value.batchNumber) {
        await executeReport(selectedBatch.value.batchNumber)
        await executeActivityReport(selectedBatch.value.batchNumber)
        await executeOrdersReport(selectedBatch.value.batchNumber)
      } else {
        logger.warn('Most recent batch has no batchNumber')
      }
    } else {
      logger.warn('No batch items found in response')
      batchList.value = []
    }
  } catch (err: any) {
    error.value = err.message || 'Failed to load batch history'
    logger.error('Error loading batch history:', err)
  } finally {
    loading.value = false
  }
}

// Handle batch selection change - re-execute report with new batch
async function onBatchChange(event: any) {
  logger.debug('Batch selection changed:', selectedBatch.value)

  if (selectedBatch.value && selectedBatch.value.batchNumber) {
    logger.info('Executing report for batch:', selectedBatch.value.batchName)

    // Re-execute the report for the currently selected tab
    if (selectedTab.value === 0) {
      // Adjustments tab
      await executeReport(selectedBatch.value.batchNumber)
    } else if (selectedTab.value === 1) {
      // Activity tab
      await executeActivityReport(selectedBatch.value.batchNumber)
    } else if (selectedTab.value === 2) {
      // Orders tab
      await executeOrdersReport(selectedBatch.value.batchNumber)
    }
  } else {
    logger.warn('Selected batch has no batchNumber')
    reportData.value = []
    activityReportData.value = []
    ordersReportData.value = []
  }
}

// Toggle Include Team for Orders tab
async function toggleIncludeTeam() {
  includeTeam.value = !includeTeam.value
  logger.info(`Include team toggled to: ${includeTeam.value}`)

  // Re-execute the orders report with the new setting
  if (selectedBatch.value && selectedBatch.value.batchNumber) {
    await executeOrdersReport(selectedBatch.value.batchNumber)
  }
}

// Handle tab selection change
async function onTabSelect(...args: any[]) {
  const event = args[0]
  selectedTab.value = event.selected
  logger.debug('Tab changed to:', selectedTab.value)

  // Execute the report for the newly selected tab with the current batch
  if (selectedBatch.value && selectedBatch.value.batchNumber) {
    if (selectedTab.value === 0) {
      // Adjustments tab
      await executeReport(selectedBatch.value.batchNumber)
    } else if (selectedTab.value === 1) {
      // Activity tab
      await executeActivityReport(selectedBatch.value.batchNumber)
    } else if (selectedTab.value === 2) {
      // Orders tab
      await executeOrdersReport(selectedBatch.value.batchNumber)
    }
  }
}

// Initialize on mount
async function initialize() {
  try {
    loading.value = true
    error.value = ''

    // Step 1: Load report definitions for all tabs
    await loadReportDefinition()
    await loadActivityReportDefinition()
    await loadOrdersReportDefinition()

    // Step 2: Load batch list (which will auto-select and execute report)
    await loadBatchList()
  } catch (err: any) {
    logger.error('Error initializing:', err)
    /* TEAU-1239: Mock data commented out - was for testing only
    setupMockData()
    */
  } finally {
    loading.value = false
  }
}

/* TEAU-1239: Mock data commented out - was for testing only
function setupMockData() {
  error.value = ''

  // Mock batch list
  batchList.value = [
    { batchName: '2026-03 (Mock)', batchNumber: 'MOCK001' },
    { batchName: '2026-02 (Mock)', batchNumber: 'MOCK002' },
  ]
  selectedBatch.value = batchList.value[0]

  // Mock Adjustments tab report definition and data
  reportDefinition.value = {
    localizedReportName: 'Adjustments',
    columns: [
      { columnName: 'BeeNumber', localizedColumnName: 'Berater-Nr.', displayOrder: 1, isFilterOnly: false, dataType: 'StringDataType' },
      { columnName: 'Name', localizedColumnName: 'Name', displayOrder: 2, isFilterOnly: false, dataType: 'StringDataType' },
      { columnName: 'AdjustmentDate', localizedColumnName: 'Datum', displayOrder: 3, isFilterOnly: false, dataType: 'DateDataType' },
      { columnName: 'AdjustmentAmount', localizedColumnName: 'Betrag', displayOrder: 4, isFilterOnly: false, dataType: 'DecimalDataType' },
    ]
  } as any
  reportData.value = [
    { BeeNumber: 123456, Name: 'Evan Christianson', AdjustmentDate: '15/03/2026', AdjustmentAmount: FormatCurrency(56.90, null) },
    { BeeNumber: 789012, Name: 'Marie Dupont', AdjustmentDate: '10/02/2026', AdjustmentAmount: FormatCurrency(134.75, null) },
  ]

  // Mock Activity tab report definition and data
  activityReportDefinition.value = {
    localizedReportName: 'Business Details',
    columns: [
      { columnName: 'FieldName', localizedColumnName: 'Feld', displayOrder: 1, isFilterOnly: false },
      { columnName: 'Value', localizedColumnName: 'Wert', displayOrder: 2, isFilterOnly: false },
    ]
  } as any
  activityReportData.value = [
    { fieldName: 'Persönliche Verkäufe', value: FormatCurrency(1234.56, null) },
    { fieldName: 'Team-Verkäufe', value: FormatCurrency(5678.90, null) },
    { fieldName: 'Neue Rekrutierungen', value: '3' },
    { fieldName: 'Aktive Berater', value: '12' },
  ]

  // Mock Orders tab report definition and data
  ordersReportDefinition.value = {
    localizedReportName: 'Personal Orders',
    columns: [
      { columnName: 'OrderNumber', localizedColumnName: 'Bestell-Nr.', displayOrder: 1, isFilterOnly: false, dataType: 'StringDataType' },
      { columnName: 'OrderDate', localizedColumnName: 'Datum', displayOrder: 2, isFilterOnly: false, dataType: 'DateDataType' },
      { columnName: 'CustomerName', localizedColumnName: 'Kunde', displayOrder: 3, isFilterOnly: false, dataType: 'StringDataType' },
      { columnName: 'OrderTotal', localizedColumnName: 'Gesamt', displayOrder: 4, isFilterOnly: false, dataType: 'DecimalDataType' },
    ]
  } as any
  ordersReportData.value = [
    { OrderNumber: '558F05C5409454', OrderDate: '20/03/2026', CustomerName: 'Evan Christianson', OrderTotal: FormatCurrency(156.90, null) },
    { OrderNumber: '627CFF92365B3A', OrderDate: '12/02/2026', CustomerName: 'Marie Dupont', OrderTotal: FormatCurrency(89.90, null) },
  ]

  logger.info('Mock data loaded for ConsultantActivity')
}
*/

onMounted(() => {
  initialize()
})
</script>

<style lang="scss" scoped>
.consultant-activity {
  padding: $card-padding;
}

.loading, .error {
  padding: $card-padding;
  text-align: center;
}

.error {
  color: red;
}

.content {
  margin-top: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  @include font-weight-semibold;
  display: block;
  margin-bottom: 8px;
  color: #333;
}

.activity-tabstrip {
  margin-top: 20px;
}

.tab-content {
  padding: $spacing-5 0;
}

.report-content {
  background: white;
  border-radius: $radius-sm;
  box-shadow: 0 2px 4px rgba(black, 0.1);
}

.no-data {
  padding: $spacing-10 $spacing-5;
  text-align: center;
  color: #666;
  font-style: italic;
  background: var(--vt-c-white-soft);
  border-radius: $radius-sm;
  border: 1px dashed #cccccc;
}

.placeholder-content {
  padding: $spacing-16 $spacing-5;
  text-align: center;
  @include typography-body;
  color: #999999;
  background: var(--vt-c-white-soft);
  border-radius: $radius-sm;
  border: 2px dashed #e0e0e0;
}

.placeholder-content p {
  margin: 0;
  font-style: italic;
}

.orders-toolbar {
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-start;
}

.team-toggle-button {
  min-width: 150px;
}
</style>
